import React, { useContext } from 'react';
import { View, SxProp, ActivityIndicator } from 'dripsy';

import { Icon } from '@/design-system/components/icons/Icon';
import { TextBody, TextCaption } from '@/design-system/components/typography';
import { UniButton } from '@/design-system/components/buttons';
import { useStore } from 'store/store';
import { Pagination } from '@/design-system/components/pagination/Pagination';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { CurrenciesIcon } from '@/features/earn/components';
import { useNavigation } from '@react-navigation/native';
import { RootStackRoutes } from 'types';
import { EarnStackParamList, EarnStackRoutes, EarnTabParamList, EarnTabRoutes } from '@/features/earn/earn.navigator';
import { CurrencyIconVariant } from '@/features/earn/components/CurrenciesIcon';
import { PageItemSkeleton } from '@/features/earn/components/PageItemSkeleton';
import { useTranslation } from 'react-i18next';
import { getEarnMaturityDateTranslationKey } from '@/features/earn/utils/getMaturityDate';
import { UserEarnActionListResponseDataItem } from '@utx/utx-client';
import { CurrenciesContext } from '@/currencies/currency.context';
import { TableView } from '@/design-system/components/table/TableView';
import { getEarnCategoryTranslationKey } from '@/features/earn/utils/getEarnCategoryTranslationKey';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

export interface PositionsStakingProps {
  loading: boolean;
  paginatedProps: {
    currentPage: number;
    totalPages: number;
    onNextPage: () => void;
    onPrevPage: () => void;
  };
  onUpdate: () => void;
  refreshPortfolio: () => void;
  data?: Array<UserEarnActionListResponseDataItem>;
  showAmount?: boolean;
}

export const PositionsStaking: React.FC<PositionsStakingProps> = ({
  loading,
  paginatedProps,
  data,
  onUpdate,
  refreshPortfolio,
  showAmount: propsShowAmount
}) => {
  const store = useStore();
  const navigation = useNavigation();
  const earnTabNavigation = useNavigation<NativeStackNavigationProp<EarnTabParamList>>();
  const { t } = useTranslation();
  const { getDecimalByAsset } = useContext(CurrenciesContext);

  const showAmount = propsShowAmount !== undefined ? propsShowAmount : store.showAmount;

  const onRowPressed = (item: UserEarnActionListResponseDataItem) => {
    navigation.navigate(RootStackRoutes.Earn, {
      screen: EarnStackRoutes.SubscriptionDetails,
      params: { subscription: item, onUpdate, refreshPortfolio }
    });
  };

  if (loading && !data) {
    return (
      <View sx={styles.container}>
        <PageItemSkeleton />
      </View>
    );
  }

  if (!loading && data && data.length === 0) {
    return (
      <View sx={styles.container}>
        <View
          sx={{
            alignItems: 'center'
          }}>
          <Icon name="listZeroState" height={60} width={60} />
          <View sx={{ height: '$4' }} />
          <TextBody secondary>{t('earn-invest::glossary.no-position-currently')}</TextBody>
          <View sx={{ height: '$5' }} />
          <UniButton
            label={t('earn-invest::glossary.discover')}
            onPress={() => {
              earnTabNavigation.navigate(EarnTabRoutes.Discover);
            }}
            sx={{ paddingX: '$12' }}
          />
        </View>
      </View>
    );
  }

  return (
    <View sx={styles.container}>
      <View sx={styles.assetsContainer}>
        <TableView<UserEarnActionListResponseDataItem>
          loading={loading && data?.length === 0}
          data={data || []}
          onRowPressed={onRowPressed}
          columns={[
            {
              title: t('earn-invest::glossary.subscriptions'),
              index: 'asset',
              minWidth: 300,
              render: (item) => (
                <CurrenciesIcon
                  asset={item.asset}
                  title={item.offerName}
                  subText={t(getEarnCategoryTranslationKey(item.category))}
                  variant={CurrencyIconVariant.native}
                  textSize="textBody"
                  subTextSx={{ color: '$textPrimary' }}
                  textBold={false}
                  textSemiBold
                />
              )
            },
            {
              title: t('earn-invest::glossary.est-apy'),
              index: 'estimatedApyPercent',
              formatValue: (item) => formatAmountByDecimals({ value: item.estimatedApyPercent, decimals: 2, suffix: '%' }),
              align: 'right',
              minWidth: 70
            },
            {
              title: t('earn-invest::glossary.subscription-amount'),
              index: 'amount',
              align: 'right',
              render: (item) => (
                <View sx={{ flex: 1, alignItems: 'flex-end' }}>
                  <TextBody bold label sx={{ textAlign: 'right', color: '$textHighlight' }}>
                    {showAmount &&
                      formatAmountByDecimals({
                        value: item.amount,
                        decimals: getDecimalByAsset(item.asset),
                        suffix: item.asset
                      })}
                    {!showAmount && '**********'}
                  </TextBody>
                  <TextCaption secondary sx={{ textAlign: 'right' }}>
                    {showAmount &&
                      formatAmountByDecimals({
                        value: item.amountUsd,
                        decimals: 2,
                        prefix: '≈',
                        suffix: 'USD'
                      })}
                    {!showAmount && '**********'}
                  </TextCaption>
                </View>
              )
            },
            {
              title: t('earn-invest::glossary.rewards-accrued'),
              index: 'totalRewardsAccrued',
              align: 'right',
              render: (item) => (
                <View sx={{ flex: 1, alignItems: 'flex-end' }}>
                  <TextBody bold label sx={{ textAlign: 'right', color: '$textHighlight' }}>
                    {showAmount &&
                      formatAmountByDecimals({
                        value: item.totalRewardsAccrued,
                        decimals: getDecimalByAsset(item.asset),
                        suffix: item.asset
                      })}
                    {!showAmount && '**********'}
                  </TextBody>
                  <TextCaption secondary sx={{ textAlign: 'right' }}>
                    {showAmount &&
                      formatAmountByDecimals({
                        value: item.totalRewardsAccruedUsd,
                        decimals: 2,
                        prefix: '≈',
                        suffix: 'USD'
                      })}
                    {!showAmount && '**********'}
                  </TextCaption>
                </View>
              )
            },
            {
              title: t('earn-invest::glossary.maturity'),
              index: 'maturityDate',
              minWidth: 200,
              render: (item) => {
                const isFlexible = item.maturityDate === null;
                const autoRenewEnabled = item.autoRenew;
                const { key, option } = getEarnMaturityDateTranslationKey(item.maturityDate);
                return (
                  <View sx={{ flex: 1, flexDirection: 'row', gap: '$3', alignItems: 'center' }}>
                    <TextBody>{t(key, option)}</TextBody>
                    {!isFlexible && autoRenewEnabled && <Icon name="autoRenewalEnabled" />}
                  </View>
                );
              },
              sx: { pl: '$10' }
            }
          ]}
        />
        <View sx={styles.pagination}>
          {data && data.length > 0 && (
            <Pagination
              currentPage={paginatedProps.currentPage}
              totalPages={paginatedProps.totalPages}
              onNextPage={paginatedProps.onNextPage}
              onPrevPage={paginatedProps.onPrevPage}
            />
          )}
        </View>
      </View>
    </View>
  );
};

enum StyleName {
  container = 'container',
  assetsContainer = 'assetsContainer',
  tableHeader = 'tableHeader',
  pagination = 'pagination'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    flex: 1,
    backgroundColor: '$backgroundPrimary',
    overflowY: 'visible'
  },
  assetsContainer: {},
  tableHeader: {
    flexDirection: 'row'
  },
  pagination: {
    mt: '$5',
    flexDirection: 'row',
    justifyContent: 'flex-end'
  }
};
